import { Injectable, Injector, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Expense } from '../models/expense.model';
import type { IExpensesStore } from './expenses-store.interface';

@Injectable({ providedIn: 'root' })
export class ExpensesMockStore implements IExpensesStore {
  private readonly _expenses = signal<readonly Expense[]>([]);
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);
  private readonly injector = inject(Injector);

  readonly expenses = this._expenses.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._expenses().length);
  readonly paidCount = computed(() => this._expenses().filter(e => e.isPaid).length);
  readonly unpaidCount = computed(() => this._expenses().filter(e => !e.isPaid).length);
  readonly totalAmount = computed(() => this._expenses().reduce((s, e) => s + (e.amount ?? 0), 0));

  async refresh(): Promise<void> {
    this._loading.set(true);
    await Promise.resolve();
    this._loading.set(false);
  }

  getExpensesForProperty(propertyUID: string): readonly Expense[] {
    return this._expenses().filter(e => e.propertyUID === propertyUID);
  }

  getExpensesForBuilding(buildingUID: string): readonly Expense[] {
    return this._expenses().filter(e => e.buildingUID === buildingUID);
  }

  expensesForProperty$(propertyUID: string): Observable<readonly Expense[]> {
    return toObservable(this._expenses, { injector: this.injector }).pipe(
      map(list => list.filter(e => e.propertyUID === propertyUID))
    );
  }

  expensesForBuilding$(buildingUID: string): Observable<readonly Expense[]> {
    return toObservable(this._expenses, { injector: this.injector }).pipe(
      map(list => list.filter(e => e.buildingUID === buildingUID))
    );
  }

  getExpenseById(expenseUID: string): Expense | null {
    return this._expenses().find(e => e.expenseUID === expenseUID) ?? null;
  }

  async getExpenseByIdOnce(expenseUID: string): Promise<Expense | null> {
    await Promise.resolve();
    return this._expenses().find(e => e.expenseUID === expenseUID) ?? null;
  }

  async addExpense(expense: Expense): Promise<string> {
    const uid = `mock-exp-${Math.random().toString(36).slice(2, 10)}`;
    const now = new Date();
    const next = Object.assign(
      Object.create(Object.getPrototypeOf(expense) || Object.prototype),
      expense,
      { expenseUID: uid, createdAt: now, updatedAt: now }
    ) as Expense;
    this._expenses.set([...this._expenses(), next]);
    return uid;
  }

  async updateExpense(expense: Expense): Promise<void> {
    if (!expense.expenseUID) throw new Error('expenseUID required');
    const uid = expense.expenseUID;
    this._expenses.set(
      this._expenses().map(e => (e.expenseUID === uid ? ({ ...e, ...expense, updatedAt: new Date() } as Expense) : e))
    );
  }

  async deleteExpense(expenseUID: string): Promise<void> {
    this._expenses.set(this._expenses().filter(e => e.expenseUID !== expenseUID));
  }

  /** Test helper. */
  _seed(expenses: readonly Expense[]): void {
    this._expenses.set(expenses);
  }
}
